import React, { createContext, useContext, useEffect, useState } from "react";
import { ISearchImportHistoryForm } from "../importHistory/importHistoryTab";
import { supplierInventoryImportApi } from "@/main/api";
import { SupplierInventoryImportDataResponse } from "@/main/models/response";
import { getRequestGrid } from "../importHistory/importHistoryTab";
import { useSearchParams } from "react-router-dom";
import { isNullUndefined } from "@/main/services/utils";

interface ITabsInventoryContext {
	tabCurrent: number;
	setTabCurrent: (value: number) => void;
	searchGridHistory: (form: any) => void;
	gridData: IGridData;
	setGridData: (form: IGridData) => void;
	hasShowMore: boolean;
	setHasShowMore: (hasShowMore: boolean) => void;
	gridDataEmpty: boolean;
	setGridDataEmpty: (empty: boolean) => void;
	exporting: boolean;
	setExporting: (value: boolean) => void;
	uploading: boolean;
	setUploading: (value: boolean) => void;
	changeTab: (tab: number) => void;
	setOpenModalReturnImport: (value: boolean) => void;
	setDataReturnImport: (value: SupplierInventoryImportDataResponse | null) => void;
	dataReturnImport: SupplierInventoryImportDataResponse | null;
	openModalReturnImport: boolean;
}

interface ITabsInventoryProvider {
	children: React.ReactNode;
}

interface IGridData {
	data: SupplierInventoryImportDataResponse[];
	totalItems: number;
	isLoading: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

const TabsInventoryContext = createContext<ITabsInventoryContext>({
	tabCurrent: 0,
	setTabCurrent: () => {},
	searchGridHistory: () => {},
	gridData: INITIAL_GRID_STATE,
	setGridData: () => {},
	hasShowMore: false,
	setHasShowMore: () => {},
	gridDataEmpty: false,
	setGridDataEmpty: () => {},
	exporting: false,
	setExporting: () => {},
	uploading: false,
	setUploading: () => {},
	changeTab: () => {},
	setDataReturnImport: () => {},
	setOpenModalReturnImport: () => {},
	dataReturnImport: null,
	openModalReturnImport: false,
});

function TabsInventoryProvider({ children }: ITabsInventoryProvider) {
	const [searchParams, setSearchParams] = useSearchParams();
	const tabQueryParams = searchParams.get("tab");
	const INITIAL_CURRENT_TAB = tabQueryParams ? parseInt(tabQueryParams) : 0;
	const [tabCurrent, setTabCurrent] = useState(INITIAL_CURRENT_TAB);
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [gridDataEmpty, setGridDataEmpty] = useState<boolean>(false);
	const [hasShowMore, setHasShowMore] = useState<boolean>(false);
	const [exporting, setExporting] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [openModalReturnImport, setOpenModalReturnImport] = useState(false);
	const [dataReturnImport, setDataReturnImport] = useState<SupplierInventoryImportDataResponse | null>(null);

	function searchGridHistory(formInputs: ISearchImportHistoryForm) {
		setGridDataEmpty(false);
		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});
		const paramsFormatted = {
			...getRequestGrid(formInputs),
			startDate: formInputs.startDate,
			finalDate: formInputs.finalDate,
		};
		supplierInventoryImportApi.getSearchGrid(paramsFormatted).then((response) => {
			setGridData({
				isLoading: false,
				totalItems: response.data.totalNumberOfRecords,
				data: response.data.results,
			});
			setHasShowMore(response.data.results.length < response.data.totalNumberOfRecords);
			if (response.data.results.length === 0) {
				setGridDataEmpty(true);
			}
		});
	}

	function changeTab(tab: number) {
		setSearchParams(""); // clear url to queryParams.tsx save new query params
		setTabCurrent(tab);
	}

	// UseEffect to change tab when query params change
	useEffect(() => {
		if (isNullUndefined(tabQueryParams)) return;
		const canChangeTab = tabCurrent !== Number(tabQueryParams);
		if (canChangeTab) {
			setTabCurrent(parseInt(tabQueryParams));
		}
	}, [tabQueryParams]);

	return (
		<TabsInventoryContext.Provider
			value={{
				tabCurrent,
				setTabCurrent,
				searchGridHistory,
				gridData,
				setGridData,
				hasShowMore,
				setHasShowMore,
				gridDataEmpty,
				setGridDataEmpty,
				exporting,
				setExporting,
				uploading,
				setUploading,
				changeTab,
				setDataReturnImport,
				setOpenModalReturnImport,
				dataReturnImport,
				openModalReturnImport,
			}}
		>
			{children}
		</TabsInventoryContext.Provider>
	);
}

const useTabsInventoryContext = () => useContext(TabsInventoryContext);

export { TabsInventoryProvider, useTabsInventoryContext };
